/**
 * When the voice service gives up on a turn (e.g. EAP_TIMEOUT), EAP may still finish and
 * save the answer. These helpers detect that saved answer in the conversation history.
 */

const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Number of times `userText` appears as a user message directly followed by a non-empty answer. */
export const countSavedAnswers = (messages, userText) => {
  const target = normalize(userText);
  if (!target || !Array.isArray(messages)) return 0;
  let count = 0;
  messages.forEach((m, i) => {
    const next = messages[i + 1];
    if (m?.role === 'user' && normalize(m.content) === target && next && next.role !== 'user' && normalize(next.content)) {
      count += 1;
    }
  });
  return count;
};

/**
 * Poll `fetchMessages` until a new answer to `userText` is saved. The first successful fetch
 * sets the baseline, so an identical question answered earlier doesn't count.
 * @returns {Promise<boolean>} true if found before timeout/cancel.
 */
export async function waitForSavedAnswer(fetchMessages, userText, { intervalMs = 3000, timeoutMs = 120000, isCancelled = () => false } = {}) {
  const deadline = Date.now() + timeoutMs;
  let baseline = null;
  while (Date.now() < deadline) {
    if (isCancelled()) return false;
    try {
      const count = countSavedAnswers(await fetchMessages(), userText);
      if (baseline === null) baseline = count;
      else if (count > baseline) return !isCancelled();
    } catch {
      /* transient; keep polling */
    }
    if (isCancelled()) return false;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return false;
}
